// Finding the employer in the carrier registry.
//
// The way out — typing the employer, or saying it was not a driving job — is
// in the sheet's footer, and only once a search has answered. RUM showed 39%
// of typed employers came from drivers who never searched at all, and a typed
// employer has no DOT number to find a verification contact by. Searching
// first costs a non-driving job one query; skipping it cost us the carrier.

import { CalendarOff, Search, X } from 'lucide-react'

import type { CarrierChoice } from '../lib/entryFlow'

import { ResultRow } from './ResultRow'

export interface SearchStepProps {
  query: string
  onQuery: (query: string) => void
  results: CarrierChoice[]
  searching?: boolean
  /** True once a query has actually run for this text. */
  searched?: boolean
  /** The registry did not answer — different from finding nothing. */
  failed?: boolean
  onSelect: (carrier: CarrierChoice) => void
  /**
   * What was typed reads as time between jobs ("N/A", "Unemployment"). Offered
   * the gap entry instead of an employer.
   */
  gapLike?: boolean
  onSwitchToUnemployment?: () => void
}

const MIN_QUERY = 2

/** "HQ Lowell, AR · DOT 39874 · 12,000 trucks", leaving out what is unknown. */
export const carrierDetails = (carrier: CarrierChoice): string => {
  const place = [carrier.city, carrier.state].filter(Boolean).join(', ')
  const trucks = carrier.powerUnits
    ? `${carrier.powerUnits.toLocaleString('en-US')} ${
        carrier.powerUnits === 1 ? 'truck' : 'trucks'
      }`
    : null
  return [
    place ? `HQ ${place}` : null,
    carrier.dotNumber ? `DOT ${carrier.dotNumber}` : null,
    trucks,
  ]
    .filter(Boolean)
    .join(' · ')
}

export const SearchStep = ({
  query,
  onQuery,
  results,
  searching = false,
  searched = false,
  failed = false,
  onSelect,
  gapLike = false,
  onSwitchToUnemployment,
}: SearchStepProps) => {
  const longEnough = query.trim().length >= MIN_QUERY
  const settled = longEnough && searched && !searching

  return (
    <>
      <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 shadow-sm">
        <Search size={14} className="flex-none text-doubleNickel-gray-400" />
        <input
          type="text"
          aria-label="Company name or DOT number"
          value={query}
          placeholder="Company name or DOT number"
          onChange={(event) => onQuery(event.target.value)}
          className="w-full bg-transparent text-[15px] text-doubleNickel-gray-900 outline-none placeholder:text-doubleNickel-gray-400"
        />
        {/*
          Clearing by holding backspace on a phone is slow enough that drivers
          retype rather than correct, so a mistyped carrier name becomes a
          manual entry.
        */}
        {query.length > 0 && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onQuery('')}
            className="-mr-1 flex-none rounded-full p-1 text-doubleNickel-gray-400"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {!longEnough && (
        <div className="px-1 py-3 text-center text-[11px] leading-relaxed text-doubleNickel-gray-500">
          Search by company name or DOT number. If your employer is not listed,
          you can enter it yourself after searching.
        </div>
      )}

      {gapLike && onSwitchToUnemployment && (
        <div className="flex flex-col gap-2 rounded-xl border border-doubleNickel-brand-200 bg-doubleNickel-brand-25 px-3 py-3">
          <div className="flex items-start gap-2">
            <CalendarOff
              size={15}
              className="mt-0.5 flex-none text-doubleNickel-brand-600"
            />
            <div className="text-[12px] leading-relaxed text-doubleNickel-gray-700">
              Not working during this time? Add it as time between jobs instead
              of an employer.
            </div>
          </div>
          <button
            type="button"
            onClick={onSwitchToUnemployment}
            className="w-full rounded-lg bg-doubleNickel-brand-500 p-2.5 text-center text-sm font-semibold text-white hover:bg-doubleNickel-brand-700"
          >
            Add time between jobs
          </button>
        </div>
      )}

      {longEnough && searching && (
        <div
          role="status"
          className="flex items-center justify-center gap-2 px-1 py-5 text-[12px] text-doubleNickel-gray-500"
        >
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-doubleNickel-gray-300 border-t-doubleNickel-brand-600" />
          Searching the carrier registry…
        </div>
      )}

      {settled && failed && (
        <div className="px-4 py-5 text-center">
          <div className="text-[12.5px] font-semibold text-doubleNickel-gray-700">
            The carrier registry is not responding
          </div>
          <div className="mt-1.5 text-[11px] leading-relaxed text-doubleNickel-gray-500">
            You can still enter the employer yourself and carry on.
          </div>
        </div>
      )}

      {settled && !failed && results.length === 0 && (
        <div className="px-4 py-5 text-center">
          <div className="text-[12.5px] font-semibold text-doubleNickel-gray-700">
            No carriers found
          </div>
          <div className="mt-1.5 text-[11px] leading-relaxed text-doubleNickel-gray-500">
            Only DOT-registered carriers appear here. Warehouses, local delivery
            and non-driving jobs will not.
          </div>
        </div>
      )}

      {longEnough && !searching && !failed && results.length > 0 && (
        <>
          <div className="px-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
            {results.length === 1 ? '1 carrier' : `${results.length} carriers`}
          </div>
          <div className="flex flex-col gap-1.5">
            {results.map((carrier, index) => (
              <ResultRow
                key={carrier.carrierId ?? `${carrier.name}-${index}`}
                name={carrier.name}
                details={[carrierDetails(carrier)]}
                onSelect={() => onSelect(carrier)}
              />
            ))}
          </div>
        </>
      )}
    </>
  )
}
